import {
  Badge,
  Button,
  Card,
  CardBody,
  Divider,
  Flex,
  Heading,
  Spinner,
  Stack,
  Switch,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { prettyDate } from '@pelicargo/utils';
import { useMemo, useState } from 'react';

import { TrpcOutputs } from '../../config/trpc';
import { useApiKeys } from '../../hooks/apiKey/useApiKeys';
import { AlertModal } from '../AlertModal/AlertModal';
import { GenerateApiKeyModal } from './GenerateApiKeyModal';

type ApiKey = TrpcOutputs['getApiKeysByAccount'][number];

type Props = {
  accountId: number;
};

export const ApiKeysSection = ({ accountId }: Props) => {
  const toast = useToast();
  const generateModal = useDisclosure();
  const revokeModal = useDisclosure();
  const { apiKeys = [], isLoading, isRevoking, revokeApiKey } = useApiKeys(accountId);
  const [showRevoked, setShowRevoked] = useState(false);
  const [selectedApiKey, setSelectedApiKey] = useState<ApiKey>();

  const activeApiKeys = useMemo(() => apiKeys.filter((apiKey) => !apiKey.invalidated_at), [apiKeys]);
  const revokedApiKeys = useMemo(() => apiKeys.filter((apiKey) => !!apiKey.invalidated_at), [apiKeys]);
  const visibleApiKeys = showRevoked ? [...activeApiKeys, ...revokedApiKeys] : activeApiKeys;

  const openRevokeModal = (apiKey: ApiKey) => {
    setSelectedApiKey(apiKey);
    revokeModal.onOpen();
  };

  const closeRevokeModal = () => {
    revokeModal.onClose();
    setSelectedApiKey(undefined);
  };

  const handleRevoke = async () => {
    if (!selectedApiKey) return;

    try {
      await revokeApiKey({ id: selectedApiKey.id });
      closeRevokeModal();
    } catch (error) {
      toast({
        title: 'API key could not be revoked',
        description: error?.message,
        status: 'error',
      });
    }
  };

  return (
    <Stack spacing="4">
      <Flex justify="space-between" align="start" gap="4">
        <Stack spacing="1">
          <Heading variant="h5">API keys</Heading>
          <Text textStyle="caption">Requests made with a key are authored by its default user.</Text>
        </Stack>
        <Button colorScheme="brand" onClick={generateModal.onOpen} flexShrink={0}>
          Generate key
        </Button>
      </Flex>

      <Show if={isLoading}>
        <Spinner alignSelf="center" />
      </Show>
      <Show if={!isLoading && visibleApiKeys.length === 0}>
        <Text color="gray.500">No active keys.</Text>
      </Show>
      <Show if={!isLoading && visibleApiKeys.length > 0}>
        <Stack divider={<Divider />} spacing="0">
          {visibleApiKeys.map((apiKey) => {
            const isRevoked = !!apiKey.invalidated_at;
            const defaultUser = apiKey.default_user?.full_name || `User ${apiKey.default_user?.id}`;

            return (
              <Card key={apiKey.id} variant="unstyled">
                <CardBody px="0" py="3">
                  <Flex justify="space-between" align="start" gap="4">
                    <Stack spacing="1" minW="0">
                      <Flex align="center" gap="2" wrap="wrap">
                        <Text fontWeight="semibold">{apiKey.description}</Text>
                        <Badge colorScheme={isRevoked ? 'gray' : 'green'}>{isRevoked ? 'Revoked' : 'Active'}</Badge>
                      </Flex>
                      <Text fontFamily="monospace" fontSize="sm">
                        {apiKey.key_hint}
                      </Text>
                      <Text textStyle="caption">
                        Acts as {defaultUser} · Created {prettyDate(apiKey.created_at)}
                      </Text>
                    </Stack>
                    <Show if={!isRevoked}>
                      <Button size="sm" variant="outline" colorScheme="red" onClick={() => openRevokeModal(apiKey)}>
                        Revoke
                      </Button>
                    </Show>
                  </Flex>
                </CardBody>
              </Card>
            );
          })}
        </Stack>
      </Show>

      <Show if={revokedApiKeys.length > 0}>
        <Flex align="center" gap="2">
          <Switch isChecked={showRevoked} onChange={(event) => setShowRevoked(event.target.checked)} />
          <Text fontSize="sm">{showRevoked ? 'Hide revoked' : `Show revoked (${revokedApiKeys.length})`}</Text>
        </Flex>
      </Show>

      <GenerateApiKeyModal accountId={accountId} isOpen={generateModal.isOpen} onClose={generateModal.onClose} />
      <AlertModal
        isOpen={revokeModal.isOpen}
        onClose={closeRevokeModal}
        title={`Revoke ${selectedApiKey?.description ?? 'this key'}?`}
        message="Integrations using this key stop working immediately. This can't be undone."
        btnCancel="Cancel"
        btnDelete="Revoke"
        isDeleting={isRevoking}
        handleConfirm={handleRevoke}
      />
    </Stack>
  );
};
